import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import i18n from './i18n';
import store from '@/state/store';
import { vMaska } from "maska";
import vClickOutside from "v-click-outside";
import VueApexCharts from "vue3-apexcharts";
import CKEditor from '@ckeditor/ckeditor5-vue';
import BootstrapVueNext from 'bootstrap-vue-next';
// import PrimeVue from 'primevue/config';
// import Aura from '@primeuix/themes/aura';
import { simplebar } from 'simplebar-vue'
import * as Sentry from '@sentry/vue';
import { initAuthBackend } from './helpers/authApi';
import { installLimitPaywallInterceptor } from './helpers/limitPaywall';
import { configureFakeBackend } from './helpers/fakebackend/fake-backend';
import authService from './helpers/authService';
// import VueGtag from 'vue-gtag'
import axios from 'axios'
import FlagIcon from 'vue-flag-icon'
import HelpTooltip from '@/components/HelpTooltip.vue'
import AnalyticsService from '@/services/analytics'

if (process.env.VUE_APP_DEFAULT_AUTH === "server") {
  // initFirebaseBackend(firebaseConfig);
  initAuthBackend();
} else if (process.env.VUE_APP_DEFAULT_AUTH === "fakebackend") {
  configureFakeBackend();
}

// Open the add-on paywall on structured resource-limit 403s
installLimitPaywallInterceptor();

import '@/assets/scss/app.scss';
import 'bootstrap-vue-next/dist/bootstrap-vue-next.css'
import 'bootstrap/dist/js/bootstrap.bundle';
import "leaflet/dist/leaflet.css";
import "@vueform/multiselect/themes/default.css";
import "@vueform/slider/themes/default.css";

// Load saved language from localStorage before creating the app
const loadSavedSettings = () => {
  const savedSettings = localStorage.getItem('layoutSettings')
  return savedSettings ? JSON.parse(savedSettings) : {}
}

// Set initial language in i18n
const savedSettings = loadSavedSettings()
if (savedSettings.language) {
  // Use try-catch to handle different i18n versions
  try {
    // Check what type i18n.global.locale is
    if (typeof i18n.global.locale === 'object') {
      i18n.global.locale.value = savedSettings.language
    } else {
      i18n.global.locale = savedSettings.language
    }
  } catch (error) {
    console.error("Error setting i18n global locale:", error)
    // Fallback to direct locale setting
    try {
      i18n.locale = savedSettings.language
    } catch (fallbackError) {
      console.error("Could not set i18n locale:", fallbackError)
    }
  }
}

// Survive in-page translators.
//
// Chrome's Google Translate replaces the page's text nodes with its own
// elements. Vue keeps a reference to the nodes it rendered, so its next patch
// asks a parent to remove or reposition a child it no longer owns, the DOM
// throws, and rendering stops dead in the middle of an interaction — a closing
// dialog leaves its backdrop over a frozen app, a route change never paints.
// The `notranslate` markers in app.html stop this at the source; this is the
// net for devices already set to always translate the site.
if (typeof Node !== 'undefined' && Node.prototype) {
  const nativeRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild(child) {
    if (child && child.parentNode !== this) {
      return child.parentNode ? child.parentNode.removeChild(child) : child;
    }
    return nativeRemoveChild.call(this, child);
  };

  const nativeInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      return this.appendChild(newNode);
    }
    return nativeInsertBefore.call(this, newNode, referenceNode);
  };
}

const app = createApp(App);

// Client-side error reporting. Without it a JavaScript error on a customer's
// device leaves no trace anywhere — which is why "the screen just doesn't
// open" reports could not be diagnosed. Errors only; session replay is kept
// off, and traces are sampled thinly so this stays cheap.
const SENTRY_DSN = process.env.VUE_APP_SENTRY_DSN
  || 'https://423f3ea84557b65c86b324ef50726b1c@o4508802709651456.ingest.de.sentry.io/4508802745958480';

if (process.env.NODE_ENV === 'production' && SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
    ],
    tracesSampleRate: 0.05,
    tracePropagationTargets: ['localhost', /^https:\/\/affiloop\.io\/api/],
    environment: process.env.NODE_ENV,
    release: process.env.VUE_APP_VERSION,
  });
}

// app.use(VueGtag, {
//   config: {
//     id: process.env.VUE_APP_GOOGLE_ANALYTICS_ID
//   },
//   enabled: true,
//   debug: true,
// }, router);

app.use(store)
  .use(router)
  .use(VueApexCharts)
  .use(BootstrapVueNext)
  .use(i18n)
  .directive("maska", vMaska)
  .use(CKEditor)
  // .use(PrimeVue, {
  //   theme: {
  //     preset: Aura
  //   }
  // })
  .use(simplebar)
  .use(FlagIcon)
  .use(vClickOutside)
  .component('HelpTooltip', HelpTooltip)
  .mount('#app');

// Initialize layout settings from localStorage, passing i18n instance
store.dispatch('layout/initializeLayout', { i18n });

// Initialize auth service for token monitoring
if (process.env.VUE_APP_DEFAULT_AUTH === "server") {
  authService.init();
}

// Tell the advertising pixel who is signed in.
//
// Only from this entry point: the marketing site has no signed-in user, and no
// business identifying one. AnalyticsService.identifyUser decides whether it is
// allowed to — it honours both the cookie refusal and the advertising objection
// — and is a no-op when it has nothing new to say.
//
// Three triggers, because one is not enough. At boot the pixel is usually still
// blocked, since the consent manager only releases it on `load`; so the boot
// attempt is a long shot, the `load` attempt is the one that normally lands,
// and the subscription catches a sign-in that happens later.
const identifyCurrentUser = () => AnalyticsService.identifyUser(store.getters['auth/user']);

identifyCurrentUser();
window.addEventListener('load', identifyCurrentUser);
store.subscribe((mutation) => {
  if (mutation.type === 'auth/SET_CURRENT_USER') {
    AnalyticsService.identifyUser(mutation.payload);
  }
});